import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { uploadPropertyImages } from "@/lib/storage-service";
import { useAuth } from "@/components/auth-provider";
import { toast } from "sonner";

interface PhotosDescriptionFormProps {
  values: {
    images?: string[];
    coverImage?: string;
    description?: string;
    leaseDuration?: number;
    securityDeposit?: number;
    paymentTerms?: string;
  };
  onChange: (values: {
    images?: string[];
    coverImage?: string;
    description?: string;
    leaseDuration?: number;
    securityDeposit?: number;
    paymentTerms?: string;
  }) => void;
}

export default function PhotosDescriptionForm({
  values,
  onChange,
}: PhotosDescriptionFormProps) {
  const { user } = useAuth();
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (!user) {
      toast.error("You must be logged in to upload images");
      return;
    }

    // Validate file sizes
    const maxSize = 5 * 1024 * 1024; // 5MB
    const invalidFiles = Array.from(files).filter(
      (file) => file.size > maxSize
    );
    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} file(s) exceed 5MB limit`);
      return;
    }

    setUploadingImages(true);
    setUploadProgress({ current: 0, total: files.length });

    try {
      const currentImages = values.images || [];

      // Generate temporary property ID for draft uploads
      const tempPropertyId = `draft_${user.id}_${Date.now()}`;

      // Upload images to Firebase Storage
      const uploadedUrls = await uploadPropertyImages(
        Array.from(files),
        tempPropertyId,
        user.id,
        (current, total) => {
          setUploadProgress({ current, total });
        }
      );

      if (uploadedUrls.length === 0) {
        toast.error("Failed to upload images");
        return;
      }

      const allImages = [...currentImages, ...uploadedUrls];

      // Update with all images and set cover if needed
      onChange({
        ...values,
        images: allImages,
        coverImage: values.coverImage || allImages[0],
      });

      toast.success(`${uploadedUrls.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images. Please try again.");
    } finally {
      setUploadingImages(false);
      setUploadProgress({ current: 0, total: 0 });
      // Reset input to allow re-uploading the same file
      e.target.value = "";
    }
  };

  const removeImage = (index: number) => {
    const currentImages = values.images || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    onChange({ ...values, images: newImages });

    // Update cover image if removed
    if (values.coverImage === currentImages[index]) {
      onChange({
        ...values,
        images: newImages,
        coverImage: newImages[0] || undefined,
      });
    }
  };

  const setCoverImage = (url: string) => {
    onChange({ ...values, coverImage: url });
  };

  const remainingChars = 500 - (values.description?.length || 0);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          Photos & Details
        </h2>
        <p className="mt-2 text-base text-muted-foreground">
          Add photos, description, and lease terms for your property
        </p>
      </div>

      {/* Photo Upload */}
      <div className="space-y-4">
        <div>
          <Label className="text-base font-medium">
            Property Photos <span className="text-red-500">*</span>
          </Label>
          <p className="text-sm text-muted-foreground mt-1">
            Upload at least 1 photo. First photo will be the cover image.
          </p>
        </div>

        <div className="border-2 border-dashed border-border rounded-xl p-8 hover:border-[#007AFF] transition-colors">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
            id="photo-upload"
            disabled={uploadingImages}
          />
          <label
            htmlFor="photo-upload"
            className={`flex flex-col items-center ${
              uploadingImages
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer"
            }`}
          >
            {uploadingImages ? (
              <>
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                </div>
                <p className="text-base font-medium text-foreground mb-1">
                  Uploading images...
                </p>
                <p className="text-sm text-muted-foreground">
                  {uploadProgress.current} of {uploadProgress.total} uploaded
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-base font-medium text-foreground mb-1">
                  Click to upload photos
                </p>
                <p className="text-sm text-muted-foreground">
                  PNG, JPG up to 5MB (will be compressed to ~1MB)
                </p>
              </>
            )}
          </label>
        </div>

        {/* Warning for data URLs */}
        {values.images &&
          values.images.some((url) => url.startsWith("data:")) && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-start gap-3">
              <svg
                className="h-5 w-5 text-yellow-600 dark:text-yellow-500 shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Images not uploaded
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Some images are still being processed. Please wait for the
                  upload to complete before submitting.
                </p>
              </div>
            </div>
          )}

        {/* Image Preview Grid */}
        {values.images && values.images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {values.images.map((url, index) => (
              <Card key={index} className="relative group overflow-hidden">
                <img
                  src={url}
                  alt={`Property ${index + 1}`}
                  className="w-full h-32 object-cover"
                />
                {values.coverImage === url && (
                  <div className="absolute top-2 left-2 bg-[#007AFF] text-white text-xs px-2 py-1 rounded">
                    Cover
                  </div>
                )}
                {url.startsWith("data:") && (
                  <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded">
                    Processing...
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  {values.coverImage !== url && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setCoverImage(url)}
                      disabled={url.startsWith("data:")}
                    >
                      <ImageIcon className="h-4 w-4 mr-1" />
                      Set Cover
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description" className="text-base font-medium">
          Property Description <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="description"
          placeholder="Describe your property in detail. Include unique features, nearby amenities, transportation access, and what makes it special..."
          value={values.description || ""}
          onChange={(e) => onChange({ ...values, description: e.target.value })}
          className="min-h-[150px] text-base resize-none"
          maxLength={500}
          required
        />
        <div className="flex justify-between text-sm">
          <p className="text-muted-foreground">Minimum 50 characters</p>
          <p
            className={
              remainingChars < 50
                ? "text-red-500 font-medium"
                : "text-muted-foreground"
            }
          >
            {remainingChars} characters remaining
          </p>
        </div>
      </div>

      {/* Lease Terms */}
      <div className="space-y-6 pt-6 border-t">
        <div>
          <h3 className="text-xl font-semibold text-foreground">Lease Terms</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Set the rental conditions for your property
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Lease Duration */}
          <div className="space-y-2">
            <Label htmlFor="leaseDuration" className="text-base font-medium">
              Lease Duration <span className="text-red-500">*</span>
            </Label>
            <Select
              value={values.leaseDuration?.toString()}
              onValueChange={(value) =>
                onChange({ ...values, leaseDuration: parseInt(value) })
              }
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12">12 months (1 year)</SelectItem>
                <SelectItem value="24">24 months (2 years)</SelectItem>
                <SelectItem value="36">36 months (3 years)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Security Deposit */}
          <div className="space-y-2">
            <Label htmlFor="securityDeposit" className="text-base font-medium">
              Security Deposit (₦) <span className="text-red-500">*</span>
            </Label>
            <Input
              id="securityDeposit"
              type="number"
              min="0"
              placeholder="e.g., 1000000"
              value={values.securityDeposit || ""}
              onChange={(e) =>
                onChange({
                  ...values,
                  securityDeposit: parseFloat(e.target.value) || 0,
                })
              }
              className="h-12 text-base"
              required
            />
            <p className="text-xs text-muted-foreground">
              Usually 1-2 months rent
            </p>
          </div>

          {/* Payment Terms */}
          <div className="space-y-2">
            <Label htmlFor="paymentTerms" className="text-base font-medium">
              Payment Terms <span className="text-red-500">*</span>
            </Label>
            <Select
              value={values.paymentTerms}
              onValueChange={(value) =>
                onChange({ ...values, paymentTerms: value })
              }
            >
              <SelectTrigger className="h-12 text-base">
                <SelectValue placeholder="Select terms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Preview */}
      {(values.images?.length ||
        values.description ||
        values.leaseDuration) && (
        <div className="p-6 bg-muted/50 border rounded-xl">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">
            Summary
          </h3>
          <div className="space-y-2">
            {values.images && values.images.length > 0 && (
              <div className="flex items-center text-sm">
                <span className="text-muted-foreground w-32">Photos:</span>
                <span className="font-medium text-foreground">
                  {values.images.length} image(s) uploaded
                </span>
              </div>
            )}
            {values.description && (
              <div className="flex items-center text-sm">
                <span className="text-muted-foreground w-32">Description:</span>
                <span className="font-medium text-foreground">
                  {values.description.length} characters
                </span>
              </div>
            )}
            {values.leaseDuration && (
              <div className="flex items-center text-sm">
                <span className="text-muted-foreground w-32">
                  Lease Duration:
                </span>
                <span className="font-medium text-foreground">
                  {values.leaseDuration} months
                </span>
              </div>
            )}
            {values.securityDeposit && (
              <div className="flex items-center text-sm">
                <span className="text-muted-foreground w-32">
                  Security Deposit:
                </span>
                <span className="font-medium text-foreground">
                  ₦{values.securityDeposit.toLocaleString()}
                </span>
              </div>
            )}
            {values.paymentTerms && (
              <div className="flex items-center text-sm">
                <span className="text-muted-foreground w-32">
                  Payment Terms:
                </span>
                <span className="font-medium text-foreground capitalize">
                  {values.paymentTerms}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
